import { Button } from "@/components/common/Button";
import Skeleton from "@/components/common/Skeleton";
import { useUserStore } from "@/modules/auth/store/user";
import { formatNumber } from "@/modules/auth/utils/format";
import { getTimeLeftUntil } from "@/modules/auth/utils/time";
import { constants } from "@/modules/calculations/consts";
import { AnalyticsEvents } from "@/shared/utils/analytics/events";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
}

const Rewards = () => {
  const { t } = useTranslation();
  const { rewards, tonPrice, price, isRewardsLoading } = useUserStore();

  const navigate = useNavigate();

  const goToWithdraw = () => {
    navigate("/withdraw");
  };

  const [withdrawInfo, setWithdrawInfo] = useState<{
    text: string;
    disabled: boolean;
  }>({
    text: "",
    disabled: true,
  });

  useEffect(() => {
    const oneTONInBTN = (1 * tonPrice) / price;
    const nextStart = getTimeLeftUntil(rewards.nextStartTimeWithdraw);
    // const nextEnd = getTimeLeftUntil(rewards.nextEndTimeWithdraw);
    // const nextStart = getTimeLeftUntil("2025-12-23T05:00:00.000Z");
    // const nextEnd = getTimeLeftUntil("2025-12-24T04:59:59.000Z");

    const getTimeParts = (time: TimeLeft): string[] => {
      const parts: string[] = [];
      if (time.days > 0)
        parts.push(t("main-page.withdraw.time.day", { count: time.days }));
      if (time.hours > 0)
        parts.push(t("main-page.withdraw.time.hour", { count: time.hours }));
      if (time.minutes > 0)
        parts.push(
          t("main-page.withdraw.time.minute", { count: time.minutes }),
        );
      return parts;
    };

    const resolveParts = (time: TimeLeft) => {
      const parts = getTimeParts(time);
      const first = parts[0] || "0";
      const second = parts[1] || "0";
      return { first, second };
    };

    if (rewards.withdrawable === true) {
      setWithdrawInfo({
        text: "",
        disabled: false,
      });
      return;
    }

    if (nextStart.days > 0 || nextStart.hours > 0 || nextStart.minutes > 0) {
      const { first, second } = resolveParts(nextStart);
      setWithdrawInfo({
        text: t("main-page.withdraw.will_available", { first, second }),
        disabled: true,
      });
      return;
    }

    if (rewards.totalRewardsBalance < oneTONInBTN) {
      setWithdrawInfo({
        text: t("main-page.withdraw.min", {
          amount: formatNumber(oneTONInBTN, {
            precision: constants.TON_DECIMALS,
          }),
          token: "BTN",
        }),
        disabled: true,
      });
      return;
    }

    // if (nextEnd.days > 0 || nextEnd.hours > 0 || nextEnd.minutes > 0) {
    //   const { first, second } = resolveParts(nextEnd);
    //   setWithdrawInfo({
    //     text: t("main-page.withdraw.open", { first, second }),
    //     disabled: false,
    //   });
    //   return;
    // }

    setWithdrawInfo({ text: "", disabled: true });
  }, [rewards, tonPrice, price, t]);

  if (isRewardsLoading)
    return (
      <div className="flex flex-col bg-bg rounded-[0.75rem]">
        <div className="flex items-center justify-between px-4">
          <div className="flex-1 flex flex-col items-start gap-0.5 py-3">
            <Skeleton className="w-8 h-5" />

            <div className="flex items-end gap-1">
              <Skeleton className="w-10 h-7" />

              <p className="text-[1.0625rem] leading-[1.375rem] text-hint pb-px">
                BTN
              </p>
            </div>

            <p className="text-[0.9375rem] leading-[1.25rem] text-subtitle-text">
              {t("main-page.rewards_balance")}
            </p>
          </div>

          <Skeleton className="w-8 h-5.5" />
        </div>

        <div className="flex flex-col gap-1 w-full p-4 pt-2">
          <Button
            onClick={goToWithdraw}
            text={t("main-page.withdraw_rewards")}
            icon="archive"
            mode="bezeled"
            className="w-full"
            // TODO: Disabled due to maintenance
            // disabled={withdrawInfo.disabled}
            disabled={true}
            analyticsEvent={AnalyticsEvents.MainPage.WithdrawRewards}
          />

          <Skeleton className="w-25 h-4" />
        </div>
      </div>
    );

  return (
    <div className="flex flex-col bg-bg rounded-xl">
      <div className="flex items-center justify-between px-4">
        <div className="flex-1 flex flex-col items-start gap-0.5 py-3">
          {rewards.tomorrowRewards > 0 && (
            <p className="text-[0.9375rem] leading-[1.25rem] text-link">
              +
              {formatNumber(rewards.tomorrowRewards, {
                precision: 3,
              })}{" "}
              BTN {t("main-page.withdraw.tomorrow")}
            </p>
          )}
          <div className="flex items-end gap-1">
            <p className="text-[1.5rem] leading-[1.75rem] font-semibold text-text">
              {formatNumber(rewards.totalRewardsBalance, {
                precision: 3,
              })}
            </p>
            <p className="text-[1.0625rem] leading-[1.375rem] text-hint pb-px">
              BTN
            </p>
          </div>

          <p className="text-[0.9375rem] leading-[1.25rem] text-subtitle-text">
            {t("main-page.rewards_balance")}
          </p>
        </div>

        <p className="text-[1.0625rem] leading-[1.375rem] text-text">
          $
          {formatNumber(rewards.totalRewardsBalanceUsd, {
            precision: 2,
          })}
        </p>
      </div>

      <div className="flex flex-col gap-1 w-full p-4 pt-2">
        <Button
          onClick={goToWithdraw}
          text={t("main-page.withdraw_rewards")}
          icon="archive"
          mode="bezeled"
          className="w-full"
          // TODO: Disabled due to maintenance
          // disabled={withdrawInfo.disabled}
          disabled={true}
          analyticsEvent={AnalyticsEvents.MainPage.WithdrawRewards}
        />
        {withdrawInfo.text && (
          <p className="pt-2 text-[0.8125rem] leading-[1rem] text-section-header-text">
            {withdrawInfo.text}
          </p>
        )}
      </div>
    </div>
  );
};

export default Rewards;
